import { Element as SlateElement, Transforms, Editor as SlateEditor, Node } from 'slate';

export const isBlockActive = (editor, format, styling) => {
  const [match] = SlateEditor.nodes(editor, {
    match: (n) => {
      if (SlateEditor.isEditor(n) || !SlateElement.isElement(n)) return false;
      if (n.type !== format) return false;
      if (styling?.alignment) {
        return n.formatting?.alignment === styling.alignment;
      }
      if (styling?.indent) {
        return !!n.formatting?.indent;
      }
      if (styling?.format) {
        return n.formatting?.format === styling.format;
      }
      return true;
    },
  });
  return !!match;
};

export const isSimpleBlockActive = (editor, format) => {
  const [match] = SlateEditor.nodes(editor, {
    match: (n) => !SlateEditor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });

  return !!match;
};

export const isMarkActive = (editor, format) => {
  const marks = SlateEditor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    SlateEditor.removeMark(editor, format);
  } else {
    SlateEditor.addMark(editor, format, true);
  }
};

export const toggleBlock = (editor, format, styling) => {
  if (!editor.selection) return;

  const structuralFormatToType = {
    li: 'list-item',
    ul: 'bulleted-list',
    ol: 'numbered-list',
  };
  const targetType = styling?.format && structuralFormatToType[styling.format];
  if (targetType) {
    const at = editor.lastSelection || editor.selection;
    const entries = Array.from(
      SlateEditor.nodes(editor, {
        at,
        match: (n) => SlateElement.isElement(n) && n.type === targetType,
      })
    );
    if (entries.length === 0) return;

    const allHaveClass = entries.every(([n]) => n.formatting?.class === styling.class);
    for (const [node, path] of entries) {
      const current = node.formatting ?? {};
      const next = { ...current };
      if (allHaveClass) {
        delete next.class;
      } else {
        next.class = styling.class;
      }
      Transforms.setNodes(editor, { formatting: next }, { at: path });
    }
    return;
  }

  let node = Node.parent(editor, editor.selection.anchor.path);
  let textFormatting = node.formatting ?? {};
  let customFormatting = {};

  if (!!styling) {
    let isIndentPress = !!styling['indent'];
    let isAlreadyIndented = !!textFormatting['indent'];

    let shouldBeIndented = isAlreadyIndented;

    if (isIndentPress) {
      shouldBeIndented = !isAlreadyIndented;
    }

    let isBlockQuotePress = styling['format'] === 'blockquote';
    let isAlreadyBlockQuote = textFormatting.format === 'blockquote';

    let shouldBeBlockQuote = isAlreadyBlockQuote;

    if (isBlockQuotePress) {
      shouldBeBlockQuote = !shouldBeBlockQuote;
    }

    customFormatting = {
      ...textFormatting,
      ...styling,
      indent: shouldBeIndented,
    };

    if (!styling.class) {
      delete customFormatting.class;
    }

    if ('format' in styling && !styling.format) {
      delete customFormatting.format;
    }

    if (shouldBeBlockQuote) {
      customFormatting.format = 'blockquote';
    } else {
      if (isAlreadyBlockQuote) {
        customFormatting.format = null;
      }
    }
  }

  const newProperties = {
    type: format,
    formatting: customFormatting,
  };

  Transforms.setNodes(editor, newProperties, {
    at: editor.lastSelection,
  });
};

const HEADING_FORMATS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'heading-1', 'heading-2', 'heading-3', 'heading-one', 'heading-two', 'heading-three']);

const isHeading = (node) => !!node && (HEADING_FORMATS.has(node.formatting?.format) || HEADING_FORMATS.has(node.type));

const lastHeadingText = (node) => {
  if (!node) return '';
  const children = node.children ?? [];
  for (let i = children.length - 1; i >= 0; i--) {
    const text = lastHeadingText(children[i]);
    if (text) return text;
  }
  if (isHeading(node)) {
    const text = Node.string(node).trim();
    if (text) return text;
  }
  return '';
};

export const findPrecedingHeadingText = (root, path) => {
  let current = [...path];
  while (current.length > 0) {
    const parent = Node.get(root, current.slice(0, -1));
    const children = parent.children ?? [];
    for (let i = current[current.length - 1] - 1; i >= 0; i--) {
      const text = lastHeadingText(children[i]);
      if (text) return text;
    }
    current = current.slice(0, -1);
  }
  return '';
};
